import {
  ActivatedRoute,
  Params,
  QueryParamsHandling,
  Router
} from "@angular/router";
import { Subscription, distinctUntilChanged, map } from "rxjs";

import { NavigationNoticeService } from "./navigation-notice.service";

/**
 * What a page has to say about its record segment. Everything else — when to
 * navigate, what a bad id does, which query params survive the hop — is the same
 * on every page and lives in {@link RecordRoute}.
 */
export interface RecordRouteConfig {
  /** The route param carrying the record id (`tracker_id`, `graphSlug`, …). */
  paramKey: string;
  /** Where the list without a record lives (`["/manager", "users"]`). */
  basePath: unknown[];
  /**
   * What a record id is appended to, when that is NOT `basePath`.
   *
   * On most pages the two are the same segment: `/manager/users` is both the list
   * and the parent of `/manager/users/:user_id`. The map and the list are the
   * exception - their list is `/map`, but their record lives one segment deeper at
   * `/map/tracker/:tracker_id` - so they say so here. Leaving it unset means the
   * record hangs off `basePath`.
   */
  recordPath?: unknown[];
  /** The URL addresses a record: show it. Resolving the id is the page's job. */
  open: (id: string) => void;
  /** The URL addresses no record: the list is what should be showing. */
  close: () => void;
  /**
   * How to treat the query string when this page navigates. `"merge"` for the
   * pages whose filters live in the URL — leaving it unset drops the query
   * string, which is what the manager pages want.
   */
  queryParamsHandling?: QueryParamsHandling;
  /**
   * Params to drop on every hop this module makes, even under `"merge"`: they
   * belong to the record being left behind (the map detail's `pos`/`hrs`).
   */
  dropQueryParams?: string[];
}

/**
 * A record segment (`/list/tracker/:tracker_id`, `/manager/users/:user_id`,
 * `/analytics/:graphSlug`) behaves the same way on all eight pages that have one:
 *
 * 1. the param says what to show — an id opens that record, no id shows the list;
 * 2. selecting a record navigates to it, and the param subscription applies it,
 *    so a click and a deep link take exactly the same path;
 * 3. an id that names nothing explains itself and drops back to the list,
 *    replacing the bad URL so Back does not walk into the same notice;
 * 4. leaving a record navigates when the URL is what put us here, and closes in
 *    place when it is not.
 *
 * All eight had written that out by hand, which is how they came to disagree
 * about which of them carries its filters over and which replaces the bad URL.
 * The page keeps what is genuinely its own: how it resolves an id, what showing a
 * record means, and what closing one means.
 *
 * Construct it where the config is known — the constructor body, or `ngOnInit`
 * for a page whose config arrives as an `@Input`.
 */
export class RecordRoute {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly notice: NavigationNoticeService,
    private readonly config: RecordRouteConfig
  ) {}

  /**
   * Let the URL drive. `paramMap` emits its current value on subscribe, so this
   * covers the deep-link load as well as later in-app navigation and
   * back/forward.
   *
   * Narrowed to this module's own param and de-duplicated, so `open`/`close` fire
   * only when THIS record's id changes. A page may hold more than one RecordRoute
   * (the map has `site_code` and `tracker_id`), and they share one `paramMap`: each
   * therefore sees the emissions caused by the other's navigation, plus the
   * query-param-only ones a filter mirror produces. Without this every such
   * navigation would re-`open` an already-open record.
   * @returns {Subscription} For the page's teardown bag.
   */
  public follow(): Subscription {
    return this.route.paramMap
      .pipe(
        map((pm) => pm.get(this.config.paramKey)),
        distinctUntilChanged()
      )
      .subscribe((id: string) => {
        if (id) {
          this.config.open(id);
        } else {
          this.config.close();
        }
      });
  }

  /** The record the URL addresses right now, or `null` on the list. */
  public currentId(): string | null {
    return this.route.snapshot.paramMap.get(this.config.paramKey);
  }

  /**
   * Show a record by navigating to it: selection follows from `follow()`, so
   * there is one open path whether the user clicked a row or typed the URL.
   */
  public goTo(id: string | number): void {
    this.navigate([...(this.config.recordPath ?? this.config.basePath), id]);
  }

  /**
   * Back to the list, dropping the record segment.
   * @param replaceUrl {boolean} True to drop the current entry from history
   * instead of pushing one. For the unknown-id fallback, where the URL being left
   * is invalid; a close keeps the default so Back still reopens the record.
   */
  public toList(replaceUrl: boolean = false): void {
    this.navigate(this.config.basePath, replaceUrl);
  }

  /** The id names no record: say so, then drop back to the list. */
  public notFound(): void {
    this.notice.itemNotFound();
    this.toList(true);
  }

  /**
   * Leave the record. From a record URL the navigation is what closes it (the
   * param subscription calls `close`); otherwise there is nothing to navigate
   * away from and the page closes in place.
   * @param closeInPlace {() => void} What closing means with no record in the URL.
   */
  public leave(closeInPlace: () => void): void {
    if (this.currentId()) {
      this.toList();
    } else {
      closeInPlace();
    }
  }

  private navigate(commands: unknown[], replaceUrl: boolean = false): void {
    this.router.navigate(commands, {
      queryParams: this.droppedParams(),
      queryParamsHandling: this.config.queryParamsHandling,
      replaceUrl
    });
  }

  /** `{ pos: null, hrs: null }` — the router's way of removing a param. */
  private droppedParams(): Params | undefined {
    const dropped: string[] = this.config.dropQueryParams;
    if (!dropped?.length) {
      return undefined;
    }
    return dropped.reduce(
      (params: Params, key: string) => ({ ...params, [key]: null }),
      {}
    );
  }
}
